import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { ArrowRight, Search, Clock, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("All");

  useEffect(() => {
    api.get("/blog").then(r => setPosts(r.data || [])).catch(()=>setPosts([])).finally(()=>setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(posts.map(p => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [posts]);

  const filtered = useMemo(() => {
    return posts.filter(p => {
      if (activeCat !== "All" && p.category !== activeCat) return false;
      if (q) {
        const s = q.toLowerCase();
        return (p.title || "").toLowerCase().includes(s) || (p.excerpt || "").toLowerCase().includes(s);
      }
      return true;
    });
  }, [posts, q, activeCat]);

  const [feature, ...rest] = filtered;

  return (
    <div className="bg-[#FBFAF6] min-h-screen" data-testid="blog-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-gray-200">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/60 via-[#FBFAF6] to-emerald-50/40 pointer-events-none"/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 border border-amber-200 text-amber-800 text-xs font-semibold uppercase tracking-wider">
            <BookOpen size={14}/> Hostro Reads
          </div>
          <h1 className="font-display font-black text-5xl md:text-7xl mt-4 tracking-tighter text-[#0E1F18]">
            The <span className="text-emerald-600">Resources</span>.
          </h1>
          <p className="mt-4 text-lg md:text-xl text-[#4A4A4A] max-w-2xl">
            Guides for students, playbooks for owners, and the occasional rant about the housing market — all in one place.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-xl bg-white rounded-2xl border border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-2 flex items-center gap-2">
            <Search size={18} className="ml-3 text-gray-400"/>
            <input
              className="flex-1 bg-transparent py-3 outline-none font-medium text-[#0E1F18] placeholder:text-gray-400"
              placeholder="Search articles..."
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              data-testid="blog-search"
            />
          </div>
        </div>
      </section>

      {/* Category pills */}
      {categories.length > 1 && (
        <section className="max-w-7xl mx-auto px-5 md:px-10 py-6 border-b border-gray-200 bg-white/50">
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="blog-categories">
            {categories.map(c => (
              <button
                key={c}
                onClick={()=>setActiveCat(c)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition border ${
                  activeCat === c
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-emerald-400 hover:text-emerald-700"
                }`}
                data-testid={`blog-cat-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Content */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-12">
        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[...Array(6)].map((_,i)=>(<div key={i} className="aspect-[4/3] rounded-2xl bg-gray-100 animate-pulse"/>))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center" data-testid="blog-empty">
            <BookOpen size={42} className="mx-auto text-gray-300"/>
            <h3 className="font-display font-black text-2xl mt-4 text-[#0E1F18]">No articles found</h3>
            <p className="text-gray-500 mt-2">Try a different search term or category.</p>
          </div>
        ) : (
          <>
            {/* Featured post — full-width hero card */}
            {feature && activeCat === "All" && !q && (
              <Link to="#" className="group block bg-white rounded-3xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300 mb-12" data-testid={`blog-featured`}>
                <div className="grid md:grid-cols-[1.4fr_1fr]">
                  <div className="aspect-[16/10] md:aspect-auto overflow-hidden bg-gray-100">
                    <img src={feature.image} alt={feature.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                  </div>
                  <div className="p-8 md:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-2">
                      <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wider border border-amber-200">Featured</span>
                      <span className="inline-block px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider border border-emerald-200">{feature.category}</span>
                    </div>
                    <h2 className="font-display font-black text-3xl md:text-4xl mt-4 leading-tight text-[#0E1F18] group-hover:text-emerald-700 transition">
                      {feature.title}
                    </h2>
                    <p className="mt-4 text-gray-600 leading-relaxed">{feature.excerpt}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-emerald-700 font-semibold group-hover:gap-3 transition-all">
                      Read article <ArrowRight size={16}/>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Other posts grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(activeCat !== "All" || q ? filtered : rest).map(p => (
                <article key={p.id} className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-emerald-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-200" data-testid={`blog-${p.id}`}>
                  <Link to="#" className="block">
                    <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="inline-block px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-bold uppercase tracking-wider border border-emerald-200">{p.category}</span>
                        {p.read_time && (
                          <span className="text-gray-400 inline-flex items-center gap-1"><Clock size={11}/> {p.read_time}</span>
                        )}
                      </div>
                      <h2 className="font-display font-black text-xl mt-3 leading-tight text-[#0E1F18] group-hover:text-emerald-700 transition line-clamp-2">{p.title}</h2>
                      <p className="text-gray-500 mt-2 text-sm line-clamp-3">{p.excerpt}</p>
                      <div className="mt-4 inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-sm group-hover:gap-2.5 transition-all">
                        Read more <ArrowRight size={14}/>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Newsletter CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-[#0E1F18] p-10 md:p-14 text-center">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl"/>
          <div className="relative">
            <h3 className="font-display font-black text-3xl md:text-4xl text-white">Never miss a guide.</h3>
            <p className="mt-3 text-white/80 max-w-lg mx-auto">Weekly digest — top PG launches, owner playbooks, food drops. No spam.</p>
            <Link to="/register" className="inline-flex items-center gap-2 mt-6 bg-emerald-500 hover:bg-emerald-400 text-[#0E1F18] font-bold px-6 py-3.5 rounded-xl transition">
              Subscribe via Hostro account <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
